import { useEffect, useMemo, useState } from 'react';

export interface PaginationResult<T> {
  /** Slice of items belonging to the current page. */
  pageItems: T[];
  /** 1-based current page index. */
  page: number;
  /** Total pages (>= 1, even when items is empty). */
  pageCount: number;
  /** Original list length. */
  total: number;
  /** Effective page size (echoed for convenience). */
  pageSize: number;
  /** Resolved range, inclusive 1-based for display ("Showing 1–12 of 47"). */
  from: number;
  to: number;
  setPage: (page: number) => void;
  next: () => void;
  prev: () => void;
}

/**
 * Generic client-side pagination over an in-memory array.
 *
 * The default page size is **12** to match what the user asked for across the
 * app. Page is 1-based to match what the UI shows. Whenever the input list
 * shrinks below the current page (e.g. after a filter or delete), `page`
 * snaps back so the user never lands on an empty page.
 */
export function usePagination<T>(items: T[] | undefined, pageSize = 12): PaginationResult<T> {
  const [page, setPage] = useState(1);
  const total = items?.length ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  // Clamp the page whenever the underlying list size shrinks.
  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  const pageItems = useMemo(() => {
    if (!items) return [];
    const start = (page - 1) * pageSize;
    return items.slice(start, start + pageSize);
  }, [items, page, pageSize]);

  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);

  return {
    pageItems,
    page,
    pageCount,
    total,
    pageSize,
    from,
    to,
    setPage: (p: number) => setPage(Math.min(Math.max(1, p), pageCount)),
    next: () => setPage((p) => Math.min(pageCount, p + 1)),
    prev: () => setPage((p) => Math.max(1, p - 1)),
  };
}
